export type ShippingDeliveryType = "HomeDelivery" | "StorePickup" | "ConvenienceStorePickup";
export type LogisticsProvider = "ECPay";
export type EcpayLogisticsSubType = "UNIMART" | "FAMI" | "HILIFE" | "UNIMARTC2C" | "FAMIC2C" | "HILIFEC2C" | "OKMARTC2C";
export type CvsStoreBrand = "SevenEleven" | "FamilyMart" | "HiLife" | "OKMart" | "Manual";

export type ShippingMethodQuote = {
  shippingMethodId: string;
  code: string;
  name: string;
  description: string | null;
  deliveryType: ShippingDeliveryType;
  logisticsProvider: LogisticsProvider | null;
  logisticsSubType: EcpayLogisticsSubType | null;
  cvsStoreBrand: CvsStoreBrand;
  cvsStoreBrandLabel: string;
  logisticsSubTypeLabel: string | null;
  requiresStoreMap: boolean;
  fee: number;
  appliedRuleName: string | null;
};

type ApiShippingMethodQuote = {
  ShippingMethodId: string;
  Code: string;
  Name: string;
  Description: string | null;
  DeliveryType?: ShippingDeliveryType;
  LogisticsProvider?: LogisticsProvider | null;
  LogisticsSubType?: EcpayLogisticsSubType | null;
  RequiresStoreMap?: boolean;
  Fee: number;
  AppliedRuleName: string | null;
};

export const cvsStoreBrandLabels: Record<CvsStoreBrand, string> = {
  SevenEleven: "7-ELEVEN",
  FamilyMart: "全家",
  HiLife: "萊爾富",
  OKMart: "OK 超商",
  Manual: "手動輸入",
};

const logisticsSubTypeLabels: Record<EcpayLogisticsSubType, string> = {
  UNIMART: "7-ELEVEN 大宗寄倉",
  FAMI: "全家大宗寄倉",
  HILIFE: "萊爾富大宗寄倉",
  UNIMARTC2C: "7-ELEVEN 門市寄取",
  FAMIC2C: "全家門市寄取",
  HILIFEC2C: "萊爾富門市寄取",
  OKMARTC2C: "OK 超商門市寄取",
};

export function getCvsStoreBrand(logisticsSubType: EcpayLogisticsSubType | null): CvsStoreBrand {
  if (logisticsSubType === "UNIMART" || logisticsSubType === "UNIMARTC2C") return "SevenEleven";
  if (logisticsSubType === "FAMI" || logisticsSubType === "FAMIC2C") return "FamilyMart";
  if (logisticsSubType === "HILIFE" || logisticsSubType === "HILIFEC2C") return "HiLife";
  if (logisticsSubType === "OKMARTC2C") return "OKMart";
  return "Manual";
}

export function getCvsStoreBrandLabel(logisticsSubType: EcpayLogisticsSubType | null): string {
  return cvsStoreBrandLabels[getCvsStoreBrand(logisticsSubType)];
}

export function getLogisticsSubTypeLabel(logisticsSubType: EcpayLogisticsSubType | null): string | null {
  return logisticsSubType ? logisticsSubTypeLabels[logisticsSubType] ?? logisticsSubType : null;
}

type ApiResponse<T> = T & { ok: boolean; message?: string };

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "資料載入失敗，請稍後再試。");
  }
  return data as T;
}

function normalize(item: ApiShippingMethodQuote): ShippingMethodQuote {
  return {
    shippingMethodId: item.ShippingMethodId,
    code: item.Code,
    name: item.Name,
    description: item.Description,
    deliveryType: item.DeliveryType || "HomeDelivery",
    logisticsProvider: item.LogisticsProvider || null,
    logisticsSubType: item.LogisticsSubType || null,
    cvsStoreBrand: getCvsStoreBrand(item.LogisticsSubType || null),
    cvsStoreBrandLabel: getCvsStoreBrandLabel(item.LogisticsSubType || null),
    logisticsSubTypeLabel: getLogisticsSubTypeLabel(item.LogisticsSubType || null),
    requiresStoreMap: item.RequiresStoreMap ?? false,
    fee: item.Fee,
    appliedRuleName: item.AppliedRuleName,
  };
}

/**
 * 結帳頁使用：依目前的商品小計，取得所有上架中的配送方式與各自「當下」的運費（已套用符合條件的活動規則）。
 * 若 API 呼叫失敗，回傳空陣列，讓結帳頁可以顯示「無法載入配送方式」的錯誤，而不是整頁掛掉。
 */
export async function fetchShippingMethods(subtotal: number): Promise<ShippingMethodQuote[]> {
  const data = await fetch(`/api/shipping-methods?Subtotal=${encodeURIComponent(String(Math.max(0, subtotal)))}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; ShippingMethods: ApiShippingMethodQuote[] }>(response));
  return data.ShippingMethods.map(normalize);
}

export type CouponDiscountType = "FreeShipping" | "FixedAmount" | "Percentage";

export type ShippingCouponVerifyResult = {
  ok: boolean;
  message: string;
  /** 折扣類型；驗證失敗時為 null。 */
  discountType: CouponDiscountType | null;
  /** 依目前小計試算的商品折抵金額（FreeShipping 類型固定為 0，因為它折抵的是運費）。 */
  discountAmount: number;
};

/**
 * 結帳頁「套用優惠碼」按鈕使用：只驗證是否可用，不會真的消耗使用次數（真正扣次數在訂單建立成功時）。
 * 回傳的 discountAmount 只是即時試算供畫面顯示，實際送出訂單時後端會用當下的真實小計重新計算一次。
 */
export async function verifyShippingCoupon(code: string, subtotal: number): Promise<ShippingCouponVerifyResult> {
  try {
    const response = await fetch("/api/shipping-coupons/verify", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ Code: code, Subtotal: Math.max(0, subtotal) }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      return { ok: false, message: data.message || "優惠碼無效，請重新確認。", discountType: null, discountAmount: 0 };
    }
    return {
      ok: true,
      message: data.message || "優惠碼套用成功！",
      discountType: (data.DiscountType as CouponDiscountType) ?? "FreeShipping",
      discountAmount: typeof data.DiscountAmount === "number" ? data.DiscountAmount : 0,
    };
  } catch {
    return { ok: false, message: "驗證失敗，請稍後再試。", discountType: null, discountAmount: 0 };
  }
}
